import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseURL from '../assets/contants/BaseURL';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser, 
  faEnvelope, 
  faBuilding, 
  faGlobe, 
  faPhone, 
  faCheckCircle,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';

function Signup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    companyName: '',
    companyWebsite: '',
    contactNumber: '',
    teamSize: '',
    industry: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [activationSent, setActivationSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [industryTypes, setIndustryTypes] = useState([]);
  const [createdOrganizationId, setCreatedOrganizationId] = useState('');

  // Fetch industry types on component mount
  useEffect(() => {
    const fetchIndustryTypes = async () => {
      try {
        const response = await fetch(`${BaseURL}Userauth/industry-types/`);
        if (response.ok) {
          const data = await response.json();
          setIndustryTypes(data);
        }
      } catch (error) {
        console.error('Error fetching industry types:', error);
      }
    };

    fetchIndustryTypes();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errorMessage) setErrorMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.username || !formData.email || !formData.companyName || 
        !formData.contactNumber || !formData.teamSize || !formData.industry) {
      setErrorMessage('Please fill in all required fields');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    setErrorMessage('');

    const apiData = {
      username: formData.username,
      email: formData.email,
      company_name: formData.companyName,
      company_website: formData.companyWebsite || null,
      contact_number: formData.contactNumber,
      team_size: formData.teamSize,
      industry_type: parseInt(formData.industry) || 1,
    };

    try {
      const response = await fetch(`${BaseURL}Userauth/organizations/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setActivationSent(true);
        if (result.data?.organization_id) {
          setCreatedOrganizationId(result.data.organization_id);
          localStorage.setItem('pendingOrganizationId', result.data.organization_id);
        }
      } else {
        setErrorMessage(result.message || 'Failed to create account. Please try again.');
      }
    } catch (error) {
      console.error('Error creating account:', error);
      setErrorMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const teamSizes = [
    { value: '', label: 'Select team size' },
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '501-1000', label: '501-1000 employees' },
    { value: '1000+', label: '1000+ employees' },
  ];

  const industries = [
    { value: '', label: 'Select industry' },
    ...industryTypes.map(type => ({
      value: type.id.toString(),
      label: type.name,
    })),
  ];

  const fallbackIndustries = [
    { value: '', label: 'Select industry' },
    { value: '1', label: 'Technology' },
    { value: '2', label: 'Healthcare' },
    { value: '3', label: 'Finance' },
    { value: '4', label: 'Education' },
    { value: '5', label: 'Retail' },
    { value: '6', label: 'Manufacturing' },
    { value: '7', label: 'Other' },
  ];

  const displayIndustries = industryTypes.length > 0 ? industries : fallbackIndustries;

  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
      width: '100%',
      maxWidth: '600px',
      padding: '40px',
      margin: '20px'
    },
    logo: {
      marginBottom: '30px',
      textAlign: 'center'
    },
    logoH2: {
      color: '#2c3e50',
      fontSize: '1.8rem',
      marginBottom: '5px'
    },
    logoP: {
      color: '#2c3e50',
      opacity: 0.7,
      fontSize: '0.9rem'
    },
    form: {
      marginTop: '20px'
    },
    inputGroup: {
      marginBottom: '20px',
      textAlign: 'left'
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '600',
      color: '#2c3e50',
      fontSize: '0.9rem'
    },
    inputField: {
      position: 'relative'
    },
    icon: {
      position: 'absolute',
      left: '15px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#2c3e50',
      opacity: 0.6,
      zIndex: 1
    },
    input: {
      width: '100%',
      padding: '12px 15px 12px 40px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '1rem',
      transition: 'all 0.3s',
      boxSizing: 'border-box',
      backgroundColor: 'white'
    },
    select: {
      width: '100%',
      padding: '12px 15px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '1rem',
      transition: 'all 0.3s',
      boxSizing: 'border-box',
      backgroundColor: 'white',
      cursor: 'pointer',
      appearance: 'none'
    },
    button: {
      width: '100%',
      padding: '12px',
      borderRadius: '4px',
      border: 'none',
      backgroundColor: '#3498db',
      color: 'white',
      fontWeight: '600',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s',
      marginTop: '10px'
    },
    buttonDisabled: {
      backgroundColor: '#95a5a6',
      cursor: 'not-allowed',
      opacity: 0.7
    },
    footer: {
      marginTop: '30px',
      fontSize: '0.9rem',
      color: '#2c3e50',
      opacity: 0.7,
      textAlign: 'center'
    },
    errorMessage: {
      color: '#e74c3c',
      marginBottom: '15px',
      fontSize: '0.9rem',
      padding: '10px',
      backgroundColor: '#fde8e8',
      borderRadius: '4px',
      display: 'flex',
      alignItems: 'center',
      gap: '10px'
    },
    successMessage: {
      color: '#27ae60',
      marginBottom: '15px',
      fontSize: '0.9rem',
      padding: '10px',
      backgroundColor: '#e8f8f5',
      borderRadius: '4px',
      textAlign: 'center'
    },
    spinner: {
      display: 'inline-block',
      width: '20px',
      height: '20px',
      border: '3px solid rgba(255,255,255,.3)',
      borderRadius: '50%',
      borderTopColor: '#fff',
      animation: 'spin 1s ease-in-out infinite',
      marginRight: '8px',
      verticalAlign: 'middle'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '15px'
    },
    fullWidth: {
      gridColumn: 'span 2'
    },
    organizationIdBox: {
      backgroundColor: '#e8f4fd',
      border: '1px solid #b8daff',
      borderRadius: '4px',
      padding: '15px',
      marginBottom: '20px',
      textAlign: 'center'
    },
    organizationIdLabel: {
      color: '#004085',
      fontSize: '0.85rem',
      marginBottom: '5px'
    },
    organizationIdValue: {
      color: '#004085',
      fontSize: '1.1rem',
      fontWeight: 'bold',
      marginBottom: '8px',
      wordBreak: 'break-all'
    },
    organizationIdNote: {
      color: '#004085',
      fontSize: '0.75rem',
      opacity: 0.8
    },
    linkButton: {
        background: 'none',
        border: 'none',
        color: '#3498db',
        cursor: 'pointer',
        fontSize: '0.9rem',
        textDecoration: 'underline',
        marginTop: '10px',
        display: 'block',
        marginLeft: 'auto',
        marginRight: 'auto',
        textAlign: 'center'
    },
    divider: {
      textAlign: 'center',
      marginTop: '20px',
      marginBottom: '20px',
      position: 'relative'
    },
    dividerLine: {
      borderTop: '1px solid #ddd',
      margin: 0
    },
    dividerText: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: 'white',
      padding: '0 10px',
      color: '#7f8c8d',
      fontSize: '0.85rem'
    },
    googleButton: {
      width: '100%',
      padding: '10px',
      borderRadius: '4px',
      border: '1px solid #ddd',
      backgroundColor: 'white',
      color: '#2c3e50',
      fontWeight: '500',
      fontSize: '0.9rem',
      cursor: 'pointer',
      transition: 'all 0.3s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px'
    },
    signinLink: {
      color: '#3498db',
      textDecoration: 'none',
      fontWeight: '600'
    }
  };

  // Add global styles for animations
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      
      input:focus, select:focus {
        outline: none;
        border-color: #3498db !important;
        box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.2) !important;
      }
      
      button:hover:not(:disabled) {
        background-color: #2980b9 !important;
      }
      
      .google-btn:hover:not(:disabled) {
        background-color: #f8f9fa !important;
        border-color: #bbb !important;
      }
      
      .signin-link:hover {
        text-decoration: underline !important;
      }
    `;
    document.head.appendChild(styleSheet);
    
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  return (
    <div style={{
      backgroundColor: '#f5f7fa',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      margin: 0,
      padding: '20px'
    }}>
      <div style={styles.container}>
        <div style={styles.logo}>
          <h2 style={styles.logoH2}>CraneMonitor Pro</h2>
          <p style={styles.logoP}>Create Your Account</p>
        </div>

        {activationSent ? (
          // Success State
          <div>
            <div style={styles.successMessage}>
              <FontAwesomeIcon icon={faCheckCircle} style={{ marginRight: '10px' }} />
              Account Created Successfully!
            </div>
            
            <div style={styles.organizationIdBox}>
              <div style={styles.organizationIdLabel}>Your Organization ID:</div>
              <div style={styles.organizationIdValue}>{createdOrganizationId}</div>
              <div style={styles.organizationIdNote}>
                Keep this ID safe. You'll need it for login and password creation.
              </div>
            </div>

            <button
              onClick={() => navigate('/approval')}
              style={styles.button}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#2980b9'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#3498db'}
            >
              Get Approval & Create Password
            </button>

            <a
              onClick={() => navigate('/login')}
              style={styles.linkButton}
              className="signup-link"
              href="#"
            >
              Return to Login
            </a>
          </div>
        ) : (
          // Signup Form
          <>
            {errorMessage && (
              <div style={styles.errorMessage}>
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>{errorMessage}</span>
              </div>
            )}

            <form style={styles.form} onSubmit={handleSubmit}>
              <div style={styles.grid}>
                {/* Username */}
                <div style={styles.inputGroup}>
                  <label style={styles.label} htmlFor="username">Username *</label>
                  <div style={styles.inputField}>
                    <FontAwesomeIcon icon={faUser} style={styles.icon} />
                    <input
                      type="text"
                      id="username"
                      name="username"
                      style={styles.input}
                      value={formData.username}
                      onChange={handleChange}
                      placeholder="Choose a username"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Email */}
                <div style={styles.inputGroup}>
                  <label style={styles.label} htmlFor="email">Email Address *</label>
                  <div style={styles.inputField}>
                    <FontAwesomeIcon icon={faEnvelope} style={styles.icon} />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      style={styles.input}
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Company Name */}
                <div style={styles.inputGroup}>
                  <label style={styles.label} htmlFor="companyName">Company Name *</label>
                  <div style={styles.inputField}>
                    <FontAwesomeIcon icon={faBuilding} style={styles.icon} />
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      style={styles.input}
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="Your company name"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Company Website */}
                <div style={styles.inputGroup}>
                  <label style={styles.label} htmlFor="companyWebsite">Company Website</label>
                  <div style={styles.inputField}>
                    <FontAwesomeIcon icon={faGlobe} style={styles.icon} />
                    <input
                      type="url"
                      id="companyWebsite"
                      name="companyWebsite"
                      style={styles.input}
                      value={formData.companyWebsite}
                      onChange={handleChange}
                      placeholder="https://example.com"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Contact Number */}
                <div style={styles.inputGroup}>
                  <label style={styles.label} htmlFor="contactNumber">Contact Number *</label>
                  <div style={styles.inputField}>
                    <FontAwesomeIcon icon={faPhone} style={styles.icon} />
                    <input
                      type="tel"
                      id="contactNumber"
                      name="contactNumber"
                      style={styles.input}
                      value={formData.contactNumber}
                      onChange={handleChange}
                      placeholder="Your phone number"
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>

                {/* Team Size */}
                <div style={styles.inputGroup}>
                  <label style={styles.label} htmlFor="teamSize">Team Size *</label>
                  <div style={styles.inputField}>
                    <select
                      id="teamSize"
                      name="teamSize"
                      style={styles.select}
                      value={formData.teamSize}
                      onChange={handleChange}
                      required
                      disabled={isLoading}
                    >
                      {teamSizes.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Industry */}
              <div style={styles.inputGroup}>
                <label style={styles.label} htmlFor="industry">Industry *</label>
                <div style={styles.inputField}>
                  <select
                    id="industry"
                    name="industry"
                    style={styles.select}
                    value={formData.industry}
                    onChange={handleChange}
                    required
                    disabled={isLoading}
                  >
                    {displayIndustries.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                type="submit"
                style={isLoading ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span style={styles.spinner}></span>
                    Creating account...
                  </>
                ) : (
                  'Create Account'
                )}
              </button>
            </form>

            <div style={styles.footer}>
              <p>
                Already have an account?{' '}
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    navigate('/login');
                  }}
                  style={styles.signinLink}
                  className="signin-link"
                >
                  Sign in
                </a>
              </p>
              <p style={{ marginTop: '15px', fontSize: '0.8rem' }}>
                © 2026 CraneMonitor Pro. All rights reserved.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Signup;